import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from 'recharts';
import html2canvas from 'html2canvas';
import api from '../../services/api';
import './TacticalMonitorApp.css';

const NAVY = '#072040', GREEN = '#91CF9B', AMBER = '#ffc107', LIGHT = '#E7E6E6', BLUE = '#599BE3';

// Active monitor scenario ('production' | 'dev'). The whole app renders through the
// same components; the scenario just re-routes reads/writes to an isolated copy of
// the composition. `withScn` merges it into a params/body object ONLY when non-
// production, so production requests are byte-identical to before this feature.
const ScenarioContext = createContext('production');
const useScenario = () => useContext(ScenarioContext);
const withScn = (scn, obj = {}) => (scn === 'production' ? obj : { ...obj, scenario: scn });

const CATEGORY_ROWS = [
  ['returns', 'Returns'],
  ['momentum', 'Momentum'],
  ['growth', 'Growth'],
  ['policy', 'Policy'],
  ['sentiment', 'Sentiment'],
];

// value → cell background (deck shading: green supportive, amber headwind,
// light grey for zero, white for empty). strong = more saturated (Total row).
function shade(v, strong = false) {
  if (v === null || v === undefined) return '#fff';
  if (Math.abs(v) < 0.005) return '#f3f4f6';
  return blend(v > 0 ? GREEN : AMBER, strong ? 0.72 : 0.5);
}
// Suggested-Tilt sub-labels (deck annotations; Credit has none).
const TILT_NOTE = { Equities: 'Growth / Defensive', Bonds: 'Within Defensives', FX: 'Overlay' };
const isoToAU = (s) => { if (!s) return ''; const [y, m, d] = String(s).slice(0, 10).split('-'); return `${d}/${m}/${y}`; };
function blend(hex, frac) {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return '#' + c.map((ci) => Math.round(frac * ci + (1 - frac) * 255).toString(16).padStart(2, '0')).join('');
}
function fmt(v, signed = false) {
  if (v === null || v === undefined) return '';
  if (Math.abs(v) < 0.005) return '0';
  const t = Math.abs(v - Math.round(v)) < 1e-9 ? v.toFixed(0) : v.toFixed(2);
  return signed && v > 0 ? '+' + t : t;
}

// ── Monitor table ─────────────────────────────────────────────────────
function MonitorTable() {
  const scn = useScenario();
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/api/tactical-monitor/table', { params: withScn(scn, { as_of: asOf }) })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [asOf, scn]);

  useEffect(() => { load(); }, [load]);

  const downloadPng = async () => {
    try {
      const r = await api.get('/api/tactical-monitor/table.png', { params: withScn(scn, { as_of: asOf }), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `tactical_monitor_${asOf}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError('PNG export failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const downloadXlsx = async () => {
    try {
      const r = await api.get('/api/tactical-monitor/table.xlsx', { params: withScn(scn, { as_of: asOf }), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `tactical_monitor_${asOf}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError('Excel export failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const blocks = data?.blocks || [];
  const bands = [];
  for (let i = 0; i < blocks.length;) { let j = i; while (j + 1 < blocks.length && blocks[j + 1].group === blocks[i].group) j++; bands.push({ group: blocks[i].group, span: j - i + 1 }); i = j + 1; }

  // groups = asset classes (Equities/Bonds/Credit/FX), each with its regions.
  let gi = 0;
  const groups = bands.map((b) => { const regions = blocks.slice(gi, gi + b.span); gi += b.span; return { group: b.group, regions }; });
  // span the group name (and merged tilt cell) across its regions AND the thin
  // region gutters between them, so the group band stays one continuous block.
  const groupSpan = (g) => g.regions.length * 3 + Math.max(0, g.regions.length - 1);
  const nCols = 2 + groups.reduce((a, g) => a + groupSpan(g), 0) + Math.max(0, groups.length - 1);
  const gTh = (k) => <th key={k} className="tm-gutter" rowSpan={3} />;   // wide gap between groups
  const gTd = (k) => <td key={k} className="tm-gutter" />;

  const triples = (cd, strong) => [['this_month', false], ['last_month', false], ['delta', true]].map(([k, signed]) => {
    const v = cd?.[k];
    return <td key={k} className="tm-num" style={{ background: shade(v, strong) }}>{fmt(v, signed)}</td>;
  });

  return (
    <div>
      <div className="tm-toolbar">
        <label>As at <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></label>
        <button className="tm-btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        <button className="tm-btn tm-btn-ghost" onClick={downloadPng}>Download PNG</button>
        <button className="tm-btn tm-btn-ghost" onClick={downloadXlsx}>Export Excel</button>
        {data && <span className="tm-muted">This: {data.this_month} · Last: {data.last_month}</span>}
      </div>
      {error && <div className="tm-alert">{error}</div>}
      {data && (
        <div className="tm-monitor-wrap">
          <div className="tm-monitor-title">Asset Allocation Monitor — {isoToAU(asOf)}</div>
          <div className="tm-table-scroll">
            <table className="tm-table tm-monitor">
              <thead>
                <tr>
                  <th rowSpan={3} className="tm-sig">Signal</th>
                  <th rowSpan={3} className="tm-note-h">Note</th>
                  {groups.map((g, i) => (
                    <React.Fragment key={g.group}>
                      <th colSpan={groupSpan(g)} className="tm-block">{g.group}</th>
                      {i < groups.length - 1 && gTh(`g${i}`)}
                    </React.Fragment>
                  ))}
                </tr>
                <tr>
                  {groups.map((g) => (
                    <React.Fragment key={g.group}>
                      {g.regions.map((r, rj) => (
                        <React.Fragment key={r.region}>
                          {rj > 0 && <th className="tm-rgutter" />}
                          <th colSpan={3} className="tm-sub">{r.label}</th>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </tr>
                <tr>
                  {groups.map((g) => (
                    <React.Fragment key={g.group}>
                      {g.regions.map((r, rj) => (
                        <React.Fragment key={r.region}>
                          {rj > 0 && <th className="tm-rgutter" />}
                          {['This Month', 'Last Month', 'Δ'].map((h) => <th key={h} className="tm-sub2">{h}</th>)}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, ri) => {
                  if (row.kind === 'category') {
                    return <tr key={ri} className="tm-cat"><td colSpan={nCols}>{row.name.toUpperCase()}</td></tr>;
                  }
                  const strong = row.kind === 'total';
                  return (
                    <tr key={ri} className={strong ? 'tm-total' : 'tm-drow'}>
                      <td className="tm-rowhead">{row.name}</td>
                      <td className="tm-note">{row.note}</td>
                      {groups.map((g, i) => (
                        <React.Fragment key={g.group}>
                          {g.regions.map((r, rj) => (
                            <React.Fragment key={r.region}>
                              {rj > 0 && <td className="tm-rgutter" />}
                              {triples(row.values[r.region], strong)}
                            </React.Fragment>
                          ))}
                          {i < groups.length - 1 && gTd(`bg${ri}-${i}`)}
                        </React.Fragment>
                      ))}
                    </tr>
                  );
                })}
                <tr className="tm-tilt-row">
                  <td className="tm-rowhead">Suggested Tilt</td><td className="tm-note" />
                  {groups.map((g, i) => {
                    const v = data.suggested_tilt?.[g.group];
                    return (
                      <React.Fragment key={g.group}>
                        <td colSpan={groupSpan(g)} className="tm-tilt-cell" style={{ background: (v === null || v === undefined) ? '#fff' : shade(v, true) }}>
                          <div className="tm-tilt-pct">{(v === null || v === undefined) ? '' : (v * 100).toFixed(1) + '%'}</div>
                          {TILT_NOTE[g.group] && <div className="tm-tilt-sub">{TILT_NOTE[g.group]}</div>}
                        </td>
                        {i < groups.length - 1 && gTd(`tg${i}`)}
                      </React.Fragment>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="tm-monitor-source">Source: Drummond Capital Partners. Positive scores are supportive of the asset class; negative scores are a headwind.</div>
        </div>
      )}
    </div>
  );
}

// ── Positioning ───────────────────────────────────────────────────────
// 8 positioning panels (Tactical_Monitor_Versus_Exposure). Each pairs a signal or
// weight (left axis) with a return index or a second series (right axis).
const GOLD = '#f0a500', GREY = '#6c757d';
const POS_CHARTS = [
  { title: 'Growth',   l: { k: 'equity_signal', name: 'Equity Signal', c: NAVY, step: true },  r: { k: 'equity_return', name: 'Equity Return', c: GOLD } },
  { title: 'Growth',   l: { k: 'growth_tilt', name: 'Growth Weight', c: GREEN, step: true },    r: { k: 'equity_signal', name: 'Equity Signal', c: GOLD, step: true } },
  { title: 'Equities', l: { k: 'equity_return', name: 'Equity Return', c: NAVY },               r: { k: 'equity_level', name: 'Equity Weight', c: GOLD, step: true } },
  { title: 'Equities', l: { k: 'equity_tilt', name: 'Equity Weight', c: NAVY, step: true },     r: { k: 'equity_signal', name: 'Equity Signal', c: GOLD, step: true } },
  { title: 'Global Equity', l: { k: 'global_tilt', name: 'Global Equity Weight', c: NAVY, step: true }, r: { k: 'global_signal', name: 'Global Equity Signal', c: GOLD, step: true } },
  { title: 'AU Equity',     l: { k: 'au_tilt', name: 'AU Equity Weight', c: NAVY, step: true },         r: { k: 'au_signal', name: 'AU Equity Signal', c: GOLD, step: true } },
  { title: 'EM Equity',     l: { k: 'em_tilt', name: 'EM Equity Weight', c: NAVY, step: true },         r: { k: 'em_signal', name: 'EM Equity Signal', c: GOLD, step: true } },
  { title: 'Bonds',    l: { k: 'bonds_signal', name: 'Bonds Signal', c: NAVY, step: true },     r: { k: 'bonds_return', name: 'Bonds Return', c: GREY } },
  { title: 'Bonds',    l: { k: 'bonds_tilt', name: 'Bonds Weight', c: NAVY, step: true },       r: { k: 'bonds_signal', name: 'Bonds Signal', c: GOLD, step: true } },
  { title: 'FX',       l: { k: 'fx_signal', name: 'Combined FX Signal', c: NAVY, step: true },  r: { k: 'fx_return', name: 'CURRENCY Return Index', c: GOLD } },
  { title: 'FX',       l: { k: 'fx_tilt', name: 'FX Weight', c: GREEN, step: true },            r: { k: 'fx_signal', name: 'Combined FX Signal', c: NAVY, step: true } },
];

function PosChart({ title, merged, l, r }) {
  return (
    <div className="tm-pos-cell">
      <h4>{title}</h4>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={merged} margin={{ top: 6, right: 6, left: -14, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="date" tick={{ fontSize: 9 }} minTickGap={46} />
          <YAxis yAxisId="l" tick={{ fontSize: 9 }} width={46} domain={['auto', 'auto']} />
          <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 9 }} width={46} domain={['auto', 'auto']} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line yAxisId="l" type={l.step ? 'stepAfter' : 'monotone'} dataKey={l.k} stroke={l.c} dot={false} name={l.name} strokeWidth={1.3} connectNulls />
          <Line yAxisId="r" type={r.step ? 'stepAfter' : 'monotone'} dataKey={r.k} stroke={r.c} dot={false} name={r.name} strokeWidth={1.3} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Suggested tilt vs the tilt the book actually holds, with a no-trade band.
// The held tilt is the live approved target minus the DS70 SAA neutral, so the
// band self-corrects against the real position rather than a stored assumption.
function TradeDecision() {
  const scn = useScenario();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bands, setBands] = useState({});
  const [saving, setSaving] = useState(false);

  // `tick` re-runs the fetch after a band save. The effect never sets state
  // synchronously, and a stale response is discarded if the scenario changed
  // while it was in flight.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    api.get('/api/tactical-monitor/tilt-decision', { params: withScn(scn) })
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        setBands(Object.fromEntries(r.data.groups.map((g) => [g.group, g.band])));
        setError(null);
      })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [scn, tick]);

  const saveBands = () => {
    setSaving(true);
    api.post('/api/tactical-monitor/decision-band',
      Object.entries(bands).map(([tilt_group, band_pp]) => ({ tilt_group, band_pp: Number(band_pp) })))
      .then(() => setTick((n) => n + 1))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setSaving(false));
  };

  const dirty = data && data.groups.some((g) => Number(bands[g.group]) !== g.band);
  const fmt = (v, dp = 2) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(dp)}`);

  if (loading) return <div className="tm-loading">Loading…</div>;
  if (error) return <div className="tm-error">{error}</div>;
  if (!data) return null;

  return (
    <div className="tm-decision">
      <div className="tm-decision-head">
        <div>
          <strong>Suggested tilt vs held position</strong>
          <div className="tm-decision-sub">
            Held = live approved target less SAA neutral for {data.reference.blender_code} ({data.reference.portfolio_code}), FY{data.saa_year}.
            Trade only when the gap exceeds the band, and then move to the band edge, not all the way to the suggested tilt.
          </div>
        </div>
        <button className="tm-btn" onClick={saveBands} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save bands'}
        </button>
      </div>
      <table className="tm-decision-table">
        <thead>
          <tr>
            <th>Group</th><th>Suggested</th><th>Held</th><th>Gap</th>
            <th>Band ±</th><th>Action</th><th>Trade to</th><th>Trade size</th>
          </tr>
        </thead>
        <tbody>
          {data.groups.map((g) => (
            <tr key={g.group} className={g.action === 'hold' ? 'hold' : 'act'}>
              <td className="tm-decision-grp">
                {g.group}
                <span className="tm-decision-parts">
                  {g.components.map((c) => `${c.name} ${c.current === null ? '—' : c.current.toFixed(1)} vs ${c.neutral.toFixed(1)}`).join(' · ')}
                </span>
              </td>
              <td>{fmt(g.suggested)}</td>
              <td>{fmt(g.held)}</td>
              <td className="tm-decision-gap">{fmt(g.gap)}</td>
              <td>
                <input type="number" step="0.25" min="0" value={bands[g.group] ?? ''}
                       onChange={(e) => setBands({ ...bands, [g.group]: e.target.value })} />
              </td>
              <td><span className={`tm-action tm-action-${g.action}`}>{g.action}</span></td>
              <td>{fmt(g.trade_to)}</td>
              <td>{g.action === 'hold' ? '—' : fmt(g.trade_size)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="tm-decision-note">
        All figures are percentage points of portfolio. A wider band trades less often and gives up
        some of the signal; the measured trade-off is in <code>Sandbox/momentum_signal_trials/SHORTLIST.md</code>.
      </div>
    </div>
  );
}

function Positioning() {
  const scn = useScenario();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const gridRef = useRef(null);
  useEffect(() => {
    setLoading(true);
    api.get('/api/tactical-monitor/positioning-charts', { params: withScn(scn) })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [scn]);

  const merged = data ? data.dates.map((d, i) => {
    const row = { date: d };
    for (const k in data.series) row[k] = data.series[k][i];
    return row;
  }) : [];

  const downloadPng = async () => {
    if (!gridRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(gridRef.current, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true });
      const a = document.createElement('a');
      a.download = `tactical_monitor_positioning_${new Date().toISOString().slice(0, 10)}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    } catch (e) {
      setError('PNG export failed: ' + (e.message || e));
    } finally {
      setExporting(false);
    }
  };

  const downloadXlsx = async () => {
    try {
      const params = withScn(scn, data?.portfolio ? { portfolio: data.portfolio } : {});
      const r = await api.get('/api/tactical-monitor/positioning-charts.xlsx', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `tactical_monitor_positioning_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Excel export failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div>
      {error && <div className="tm-alert">{error}</div>}
      {loading && <p className="tm-muted">Loading…</p>}
      {data && (
        <>
          <div className="tm-toolbar">
            <button className="tm-btn tm-btn-ghost" onClick={downloadPng} disabled={exporting}>
              {exporting ? 'Exporting…' : 'Download PNG'}
            </button>
            <button className="tm-btn tm-btn-ghost" onClick={downloadXlsx}>Export Excel</button>
            <span className="tm-muted">
              {data.portfolio}. Signals are the portal Total Score (full 5-category blend); the equity signal is
              the average across Global, AU and EM equities. Weights are active tilts (target − SAA), except the Equities
              level. Return indices rebased to 100 at {data.dates[0]}.
            </span>
          </div>
          <div className="tm-pos-grid" ref={gridRef}>
            {POS_CHARTS.map((c, i) => <PosChart key={i} title={c.title} merged={merged} l={c.l} r={c.r} />)}
          </div>
        </>
      )}
    </div>
  );
}

// ── Composition manager ───────────────────────────────────────────────
const fmtN = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
const fmtPct = (v) => (v === null || v === undefined ? '—' : (v * 100).toFixed(1) + '%');

function SignalModal({ resultId, onClose, onDeleted }) {
  const navigate = useNavigate();
  // Deleting a signal is global (removes it from the shared backtest store and
  // every scenario's composition), so it's only offered in the production view.
  const isDev = useScenario() === 'dev';
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/api/tactical-monitor/signal/${resultId}`).then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [resultId]);
  const del = async () => {
    if (!window.confirm('Delete this signal entirely? It will be removed from the monitor composition and the backtest store.')) return;
    setBusy(true); setErr(null);
    try { await api.delete(`/api/tactical-monitor/signal/${resultId}`); onDeleted(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); setBusy(false); }
  };
  const cfg = d?.config?.detail?.signal_config || {};
  return (
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tm-modal-head">
          <h3>{d?.metrics?.signal_name || 'Signal'}</h3>
          <button className="tm-btn-link" onClick={onClose}>✕</button>
        </div>
        {err && <div className="tm-alert">{err}</div>}
        {!d && !err && <p className="tm-muted">Loading…</p>}
        {d && (
          <>
            <div className="tm-modal-cols">
              <div>
                <h4>Config</h4>
                <table className="tm-kv"><tbody>
                  <tr><td>Type</td><td>{d.config.signal_type || '—'}</td></tr>
                  <tr><td>Tickers</td><td>{d.config.tickers || '—'}</td></tr>
                  <tr><td>Long / Short</td><td>{d.metrics.long_asset} / {d.metrics.short_asset}</td></tr>
                  <tr><td>Transform</td><td>{d.config.transformation_type || '—'}</td></tr>
                  <tr><td>Expression</td><td><code>{cfg.expression || '—'}</code></td></tr>
                  {cfg.custom_expressions && <tr><td>Custom</td><td><code>{Object.entries(cfg.custom_expressions).map(([k, v]) => `${k} = ${v}`).join('\n')}</code></td></tr>}
                </tbody></table>
              </div>
              <div>
                <h4>Used in</h4>
                <table className="tm-kv"><thead><tr><th>Asset class</th><th>Category</th><th>Weight</th></tr></thead><tbody>
                  {d.usage.filter((u) => u.kind === 'signal').map((u, i) => <tr key={i}><td>{u.region}</td><td>{u.category}</td><td>{fmtN(u.weight)}</td></tr>)}
                  {d.usage.filter((u) => u.kind === 'signal').length === 0 && <tr><td colSpan={3} className="tm-muted">Not weighted into any composite.</td></tr>}
                </tbody></table>
                <h4>Alpha stats <span className="tm-muted">(backtest: long asset-class index vs cash)</span></h4>
                <table className="tm-kv"><tbody>
                  <tr><td>Ann. return</td><td>{fmtPct(d.metrics.annualised_return)}</td></tr>
                  <tr><td>Information ratio</td><td>{fmtN(d.metrics.information_ratio)}</td></tr>
                  <tr><td>Hit rate</td><td>{fmtPct(d.metrics.hit_rate)}</td></tr>
                  <tr><td>Max drawdown</td><td>{fmtPct(d.metrics.max_drawdown)}</td></tr>
                  <tr><td>Turnover</td><td>{fmtN(d.metrics.annualised_turnover)}</td></tr>
                </tbody></table>
              </div>
            </div>
            <div className="tm-modal-charts">
              {[['position', 'Position', NAVY, 'stepAfter', [-1.1, 1.1]],
                ['alpha', 'Cumulative alpha (backtest)', GREEN, 'monotone', ['auto', 'auto']],
                ['drawdown', 'Drawdown (backtest)', AMBER, 'monotone', ['auto', 0]]].map(([key, label, color, type, domain]) => (
                <div key={key}>
                  <h4>{label}</h4>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={d.stream} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} minTickGap={50} />
                      <YAxis tick={{ fontSize: 9 }} domain={domain} />
                      <Tooltip />
                      <Line type={type} dataKey={key} stroke={color} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
            <div className="tm-modal-foot">
              {isDev
                ? <span className="tm-muted">Delete is disabled in the dev scenario (it affects all scenarios).</span>
                : <button className="tm-btn-link tm-del" disabled={busy} onClick={del}>Delete signal</button>}
              <div style={{ display: 'flex', gap: 8 }}>
                {d.metrics.config_id && (
                  <button className="tm-btn tm-btn-ghost"
                          onClick={() => navigate(`/apps/backtest-dashboard?config=${d.metrics.config_id}&name=${encodeURIComponent(d.metrics.signal_name || '')}`)}>
                    Open in backtester ↗
                  </button>
                )}
                <button className="tm-btn" onClick={onClose}>Close</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Picker over ALL backtest signals (production or not) — sortable table; select
// one to add into a blank cell or replace the signal currently in a cell.
const PICK_COLS = [
  ['signal_name', 'Signal', 'l'], ['signal_type', 'Type', 'l'], ['is_production', 'Prod', 'c'],
  ['information_ratio', 'IR', 'r'], ['annualised_return', 'Ann.', 'r'], ['hit_rate', 'Hit', 'r'],
  ['max_drawdown', 'Max DD', 'r'], ['long_asset', 'Long', 'l'], ['short_asset', 'Short', 'l'],
];
// Return leg per monitor asset class — mirrors the seed's REGION_RI. Constrains the
// picker so a swapped-in signal is measured against this region's asset class.
const REGION_LEG = {
  'Equity World': 'MSWRLDL(RI)', 'Equity AU': 'ASX200I(RI)', 'Equity EM': 'MSEMKF$(RI)',
  'Bonds GL': 'BMUS10Y(RI)', 'Bonds AU': 'BMAU10Y(RI)',
  'Credit GL': 'U:LQD(RI)', 'Credit AU': 'U:LQD(RI)', 'FX': 'MSERAUD',
};
function SignalPickerModal({ target, onPick, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'signal_name', dir: 1 });
  const [onlyLeg, setOnlyLeg] = useState(true);
  useEffect(() => {
    api.get('/api/tactical-monitor/all-signals').then((r) => setRows(r.data.signals || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);
  const leg = REGION_LEG[target.region];
  const clickSort = (k) => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }));
  const ql = q.trim().toLowerCase();
  const filtered = (rows || []).filter((r) => {
    if (onlyLeg && leg && r.long_asset !== leg) return false;
    return !ql || (r.signal_name || '').toLowerCase().includes(ql) || (r.signal_type || '').toLowerCase().includes(ql);
  });
  const sorted = [...filtered].sort((a, b) => {
    let x = a[sort.key], y = b[sort.key];
    if (x === null || x === undefined) return 1;
    if (y === null || y === undefined) return -1;
    if (typeof x === 'boolean') { x = x ? 1 : 0; y = y ? 1 : 0; }
    if (typeof x === 'string') return x.localeCompare(y) * sort.dir;
    return (x - y) * sort.dir;
  });
  const swap = !!target.currentRid;
  return (
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal tm-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="tm-modal-head">
          <h3>{swap ? 'Replace signal' : 'Add signal'} · {target.region} · {target.category}</h3>
          <button className="tm-btn-link" onClick={onClose}>✕</button>
        </div>
        {err && <div className="tm-alert">{err}</div>}
        <div className="tm-toolbar">
          <input placeholder="Filter by name or type…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 260 }} />
          {leg && (
            <label className="tm-muted" style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyLeg} onChange={(e) => setOnlyLeg(e.target.checked)} />
              Only {target.region} leg (long = {leg})
            </label>
          )}
          <span className="tm-muted">{sorted.length} signal{sorted.length === 1 ? '' : 's'} · click a header to sort</span>
        </div>
        {!rows && !err && <p className="tm-muted">Loading…</p>}
        {rows && (
          <div className="tm-picker-scroll">
            <table className="tm-picker">
              <thead><tr>
                {PICK_COLS.map(([k, label]) => (
                  <th key={k} className="tm-sortable" onClick={() => clickSort(k)}>
                    {label}{sort.key === k ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
                <th></th>
              </tr></thead>
              <tbody>
                {sorted.map((r) => {
                  const isCur = r.result_id === target.currentRid;
                  return (
                    <tr key={r.result_id} className={isCur ? 'tm-picker-cur' : ''}>
                      <td className="tm-l">{r.signal_name}</td>
                      <td className="tm-l">{r.signal_type || ''}</td>
                      <td className="tm-c">{r.is_production ? '✓' : ''}</td>
                      <td className="tm-r">{fmtN(r.information_ratio)}</td>
                      <td className="tm-r">{fmtPct(r.annualised_return)}</td>
                      <td className="tm-r">{fmtPct(r.hit_rate)}</td>
                      <td className="tm-r">{fmtPct(r.max_drawdown)}</td>
                      <td className="tm-l">{r.long_asset || ''}</td>
                      <td className="tm-l">{r.short_asset || ''}</td>
                      <td><button className="tm-btn" disabled={isCur} onClick={() => onPick(r.result_id)}>
                        {isCur ? 'Current' : (swap ? 'Replace' : 'Add')}</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const COMP_BLOCKS = [
  ['Equity World', 'Equities', 'Global'], ['Equity AU', 'Equities', 'Australia'], ['Equity EM', 'Equities', 'Emerging'],
  ['Bonds GL', 'Bonds', 'Global'], ['Bonds AU', 'Bonds', 'Australia'],
  ['Credit GL', 'Credit', 'Global'], ['Credit AU', 'Credit', 'Australia'], ['FX', 'FX', 'AUD'],
];
const EDIT_CATS = ['momentum', 'growth', 'sentiment'];
const TOTAL_CATS = ['returns', 'momentum', 'growth', 'policy', 'sentiment'];
const INDICATOR_ORDER = {
  momentum: ['Price', 'MACD'],
  growth: ['Growth Barometer', 'OECD', 'Credit Spreads'],
  sentiment: ['Bull vs Bear', 'RSI', 'Volatility', 'Put Call Ratio', 'Earnings Revisions', 'Yield Curve'],
};
const parseTM = (name) => { const p = (name || '').split('|'); return p.length === 4 && p[0] === 'TM' ? { region: p[1], category: p[2], indicator: p[3] } : null; };
// Policy is CMA-driven with fixed model weights (scores are set in the Inputs tab):
// standard regions average the 4 indicators; FX = Fiscal + 2·Inflation; Credit = Yield Curve + Easing.
const POLICY_INDS = ['Easing or Tightening', 'Inflation growth trade off', 'Yield Curve', 'Fiscal Policy'];
const policyWeight = (region, ind) => {
  if (region === 'FX') return { 'Fiscal Policy': 1, 'Inflation growth trade off': 2 }[ind] || 0;
  if (region === 'Credit GL' || region === 'Credit AU') return { 'Yield Curve': 1, 'Easing or Tightening': 1 }[ind] || 0;
  return 0.25;
};

function Composition() {
  const scn = useScenario();
  const isDev = scn === 'dev';
  const [members, setMembers] = useState([]);
  const [avail, setAvail] = useState([]);
  const [notes, setNotes] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setError(null);
    Promise.all([
      api.get('/api/tactical-monitor/composition', { params: withScn(scn) }),
      api.get('/api/tactical-monitor/available-signals'),
    ]).then(([c, s]) => { setMembers(c.data.members || []); setNotes(c.data.notes || {}); setAvail(s.data.signals || []); })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  };
  useEffect(() => { load(); }, [scn]);

  const rebuild = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); load(); }
    catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const indOf = (m) => { const q = parseTM(m.signal_name); return q ? q.indicator : (m.signal_name || m.member_ref); };
  // lookups keyed by region|category|indicator. sig = addable production signals
  // (by their natural name); memRid/wt = actual composition members (any category).
  const sig = {}, wt = {}, memRid = {};
  avail.forEach((s) => { const q = parseTM(s.signal_name); if (q) sig[`${q.region}|${q.category}|${q.indicator}`] = s.result_id; });
  members.filter((m) => m.member_kind === 'signal').forEach((m) => {
    const k = `${m.region}|${m.category}|${indOf(m)}`;
    wt[k] = m.weight; memRid[k] = m.member_ref;
  });
  const tw = {}, pw = {}, riw = {};
  members.filter((m) => m.member_kind === 'category').forEach((m) => { tw[`${m.region}|${m.member_ref}`] = m.weight; });
  members.filter((m) => m.member_kind === 'policy').forEach((m) => { pw[`${m.region}|${m.member_ref}`] = m.weight; });
  members.filter((m) => m.member_kind === 'cma' && m.member_ref === 'Expected Returns').forEach((m) => { riw[m.region] = m.weight; });

  // indicators per category: known order first, then any extras from avail + members
  const indicatorsFor = (cat) => {
    const found = new Set();
    avail.forEach((s) => { const q = parseTM(s.signal_name); if (q && q.category === cat) found.add(q.indicator); });
    members.filter((m) => m.member_kind === 'signal' && m.category === cat).forEach((m) => found.add(indOf(m)));
    const ordered = (INDICATOR_ORDER[cat] || []).filter((i) => found.has(i));
    [...found].sort().forEach((i) => { if (!ordered.includes(i)) ordered.push(i); });
    return ordered;
  };
  // signal-based categories present (dynamic), standard first then any custom
  const sigCats = [...new Set([...avail.map((s) => parseTM(s.signal_name)?.category), ...members.filter((m) => m.member_kind === 'signal').map((m) => m.category)])].filter((c) => c && c !== 'policy' && c !== 'returns');
  const orderedSigCats = EDIT_CATS.filter((c) => sigCats.includes(c)).concat(sigCats.filter((c) => !EDIT_CATS.includes(c)).sort());

  // group bands for the header
  const bands = []; let bi = 0;
  while (bi < COMP_BLOCKS.length) { let j = bi; while (j + 1 < COMP_BLOCKS.length && COMP_BLOCKS[j + 1][1] === COMP_BLOCKS[bi][1]) j++; bands.push({ group: COMP_BLOCKS[bi][1], span: j - bi + 1 }); bi = j + 1; }

  const saveMember = (region, category, indicator, val) => rebuild(async () => {
    const key = `${region}|${category}|${indicator}`;
    const rid = memRid[key] ?? sig[key];
    if (!rid) return;
    const isMember = wt[key] !== undefined;
    const num = val === '' ? null : Number(val);
    if (num === null || num === 0) {
      if (isMember) await api.delete('/api/tactical-monitor/composition/member', { params: withScn(scn, { region, category, result_id: rid }) });
    } else {
      await api.post('/api/tactical-monitor/composition/member', withScn(scn, { region, category, result_id: rid, weight: num, enabled: true }));
    }
  });
  const saveTotal = (region, category, val) => rebuild(() =>
    api.put('/api/tactical-monitor/composition/total-weights', withScn(scn, { region, weights: { [category]: Number(val || 0) } })));
  const savePolicy = (region, indicator, val) => rebuild(() =>
    api.post('/api/tactical-monitor/composition/policy-weight', withScn(scn, { region, indicator, weight: Number(val || 0) })));
  const saveReturns = (region, val) => rebuild(() =>
    api.post('/api/tactical-monitor/composition/returns-weight', withScn(scn, { region, weight: Number(val || 0) })));
  const saveNote = (category, indicator, note) => rebuild(() =>
    api.post('/api/tactical-monitor/composition/note', { category, indicator, note }));

  // editable Note-column text (shows in the monitor table), keyed by category|indicator.
  const noteInput = (category, indicator) => {
    const cur = notes[`${category}|${indicator}`] ?? '';
    return <input className="tm-note-input" key={`${category}|${indicator}|${cur}`} defaultValue={cur} placeholder="note" disabled={busy}
                  onBlur={(e) => e.target.value.trim() !== cur && saveNote(category, indicator, e.target.value)} />;
  };

  const wcell = (region, category, indicator) => {
    const key = `${region}|${category}|${indicator}`;
    const rid = memRid[key] ?? sig[key];
    const v = wt[key];
    return (
      <td key={region} className={v !== undefined ? 'tm-w-on' : ''}>
        {rid
          ? <span className="tm-cell">
              <input key={`${key}|${v}`} defaultValue={v ?? ''} disabled={busy}
                     onBlur={(e) => e.target.value !== String(v ?? '') && saveMember(region, category, indicator, e.target.value)} />
              <button className="tm-audit" title="Audit / delete signal" onClick={() => setAudit(rid)}>ⓘ</button>
              <button className="tm-audit" title="Swap for another backtest signal"
                      onClick={() => setPicker({ region, category, currentRid: rid, weight: v ?? 1 })}>⇄</button>
            </span>
          : <button className="tm-cell-add" title="Add a backtest signal here"
                    onClick={() => setPicker({ region, category, currentRid: null, weight: 1 })}>+</button>}
      </td>
    );
  };

  const [addTo, setAddTo] = useState({ region: 'Equity World', category: 'momentum', result_id: '', weight: 1 });
  const [audit, setAudit] = useState(null);
  const [picker, setPicker] = useState(null);   // { region, category, currentRid, weight }

  // Add the chosen backtest signal into the cell's (region, category); when replacing,
  // add first then drop the old member so the composite always keeps a constituent.
  const pickSignal = (chosen) => {
    const p = picker; setPicker(null);
    rebuild(async () => {
      await api.post('/api/tactical-monitor/composition/member',
        withScn(scn, { region: p.region, category: p.category, result_id: chosen, weight: p.weight || 1, enabled: true }));
      if (p.currentRid && p.currentRid !== chosen)
        await api.delete('/api/tactical-monitor/composition/member',
          { params: withScn(scn, { region: p.region, category: p.category, result_id: p.currentRid }) });
    });
  };

  return (
    <div>
      {audit && <SignalModal resultId={audit} onClose={() => setAudit(null)} onDeleted={() => { setAudit(null); load(); }} />}
      {picker && <SignalPickerModal target={picker} onPick={pickSignal} onClose={() => setPicker(null)} />}
      {error && <div className="tm-alert">{error}</div>}
      <p className="tm-muted">
        Composite weights per asset class — same layout as the monitor, editing the weight each signal contributes.
        Enter a weight to add a member, clear it to remove; weights are normalised when the composite is rebuilt.
        Use <strong>+</strong> in a blank cell to pick any backtest signal (production or not) to incorporate, and
        <strong> ⇄</strong> on a filled cell to swap its signal for another. The picker is sortable by any column.
      </p>

      <div className="tm-table-scroll">
        <table className="tm-comp-grid">
          <thead>
            <tr><th rowSpan={2} className="tm-rowhead">Signal</th>{bands.map((b, i) => <th key={i} colSpan={b.span} className="tm-block">{b.group}</th>)}</tr>
            <tr>{COMP_BLOCKS.map(([r, , label]) => <th key={r} className="tm-sub" title={r}>{label}</th>)}</tr>
          </thead>
          <tbody>
            {/* Returns — editable z-score input weight, blendable with signals */}
            <tr className="tm-sec"><td colSpan={COMP_BLOCKS.length + 1}>RETURNS · z-score weight + blend (scores set in Inputs)</td></tr>
            <tr>
              <td className="tm-rowhead">Expected Returns (z-score)</td>
              {COMP_BLOCKS.map(([r]) => {
                const v = riw[r] !== undefined ? riw[r] : 1;
                return <td key={r} className={v ? 'tm-w-on' : ''}>
                  <input key={`ret|${r}|${v}`} defaultValue={v ?? ''} disabled={busy || isDev}
                         title={isDev ? 'Returns-input weight is shared with production (not editable in dev)' : undefined}
                         onBlur={(e) => e.target.value !== String(v ?? '') && saveReturns(r, e.target.value)} />
                </td>;
              })}
            </tr>
            {indicatorsFor('returns').map((ind) => (
              <tr key={'returns' + ind}><td className="tm-rowhead">{ind}</td>{COMP_BLOCKS.map(([r]) => wcell(r, 'returns', ind))}</tr>
            ))}
            {/* signal-based categories — fully dynamic (add/drop flows through) */}
            {orderedSigCats.map((cat) => (
              <React.Fragment key={cat}>
                <tr className="tm-sec"><td colSpan={COMP_BLOCKS.length + 1}>{cat.toUpperCase()}</td></tr>
                {indicatorsFor(cat).map((ind) => (
                  <tr key={cat + ind}><td className="tm-rowhead"><span className="tm-rh-name">{ind}</span>{noteInput(cat, ind)}</td>{COMP_BLOCKS.map(([r]) => wcell(r, cat, ind))}</tr>
                ))}
              </React.Fragment>
            ))}
            {/* Policy — editable indicator weights (scores set in the Inputs tab) */}
            <tr className="tm-sec"><td colSpan={COMP_BLOCKS.length + 1}>POLICY · indicator weights (scores set in Inputs)</td></tr>
            {POLICY_INDS.map((ind) => (
              <tr key={ind}>
                <td className="tm-rowhead"><span className="tm-rh-name">{ind}</span>{noteInput('policy', ind)}</td>
                {COMP_BLOCKS.map(([r]) => {
                  const key = `${r}|${ind}`;
                  const v = pw[key] !== undefined ? pw[key] : policyWeight(r, ind);
                  return <td key={r} className={v ? 'tm-w-on' : ''}>
                    <input key={`${key}|${v}`} defaultValue={v ?? ''} disabled={busy || isDev}
                           title={isDev ? 'Policy weight is shared with production (not editable in dev)' : undefined}
                           onBlur={(e) => e.target.value !== String(v ?? '') && savePolicy(r, ind, e.target.value)} />
                  </td>;
                })}
              </tr>
            ))}
            {/* Total Score — category weights (editable) */}
            <tr className="tm-sec"><td colSpan={COMP_BLOCKS.length + 1}>TOTAL SCORE · category weights</td></tr>
            {TOTAL_CATS.map((cat) => (
              <tr key={cat}>
                <td className="tm-rowhead">{cat}</td>
                {COMP_BLOCKS.map(([r]) => {
                  const v = tw[`${r}|${cat}`];
                  return <td key={r} className={v !== undefined ? 'tm-w-on' : ''}>
                    <input key={`${r}|${cat}|${v}`} defaultValue={v ?? ''} disabled={busy}
                           onBlur={(e) => e.target.value !== String(v ?? '') && saveTotal(r, cat, e.target.value)} />
                  </td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="tm-add">
        <strong>Add signal / section:</strong>
        <select value={addTo.region} onChange={(e) => setAddTo({ ...addTo, region: e.target.value })}>
          {COMP_BLOCKS.map(([r]) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input list="tm-cats" value={addTo.category} placeholder="category / new section"
               onChange={(e) => setAddTo({ ...addTo, category: e.target.value })} style={{ width: 150 }} />
        <datalist id="tm-cats">{[...new Set([...orderedSigCats, ...EDIT_CATS])].map((c) => <option key={c} value={c} />)}</datalist>
        <select value={addTo.result_id} onChange={(e) => setAddTo({ ...addTo, result_id: e.target.value })}>
          <option value="">— production signal —</option>
          {avail.map((s) => <option key={s.result_id} value={s.result_id}>{s.signal_name}</option>)}
        </select>
        <input type="number" step="0.1" value={addTo.weight} style={{ width: 70 }}
               onChange={(e) => setAddTo({ ...addTo, weight: Number(e.target.value) })} />
        <button className="tm-btn" disabled={busy || !addTo.result_id || !addTo.category}
                onClick={() => rebuild(() => api.post('/api/tactical-monitor/composition/member', withScn(scn, addTo)))}>Add</button>
      </div>
      <p className="tm-muted">
        Build new signals in the Signal Dashboard and flag them production; they appear here. Type a new category name to
        start a new section. Adding a signal rebuilds that asset class’s composite via the backtest engine.
      </p>
    </div>
  );
}

// ── CMA Inputs (Excel-style wide vintage paste) ───────────────────────
function parseDate(s) {
  s = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);       // DD/MM/YYYY (Australian)
  if (m) { let [, d, mo, y] = m; if (y.length === 2) y = '20' + y; return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`; }
  return null;
}
function parseNum(s) {
  if (s == null) return null;
  s = String(s).trim().replace(/,/g, '');
  if (s === '' || s === '-' || s === '–') return null;         // dash / blank = no value
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }  // accounting negative (0.89)
  const n = Number(s);
  return Number.isNaN(n) ? null : (neg ? -n : n);
}

function VintageGrid({ kind, title }) {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [paste, setPaste] = useState('');
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    api.get(`/api/tactical-monitor/vintage/${kind}`)
      .then((r) => { setColumns(r.data.columns); setRows((r.data.rows || []).map((x) => ({ as_of: String(x.as_of).slice(0, 10), data: { ...x.data } }))); })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const appendPaste = () => {
    const added = [];
    paste.split(/\r?\n/).forEach((line) => {
      if (!line.trim()) return;
      const cells = line.split('\t');
      const as_of = parseDate(cells[0]);
      if (!as_of) return;                                        // skip header / non-date lines
      const data = {};
      columns.forEach((c, i) => { data[c] = parseNum(cells[i + 1]); });
      added.push({ as_of, data });
    });
    const map = new Map(rows.map((r) => [r.as_of, r]));
    added.forEach((r) => map.set(r.as_of, r));                   // new rows override same-date
    setRows([...map.values()].sort((a, b) => a.as_of.localeCompare(b.as_of)));
    setPaste(''); setMsg(`Parsed ${added.length} row(s) — review and Save.`);
  };

  const setCell = (ri, col, v) => setRows(rows.map((r, j) => (j === ri ? { ...r, data: { ...r.data, [col]: v } } : r)));
  const setDate = (ri, v) => setRows(rows.map((r, j) => (j === ri ? { ...r, as_of: v } : r)));
  const addRow = () => setRows([...rows, { as_of: new Date().toISOString().slice(0, 10), data: {} }]);

  // Excel-style paste starting at the field it was pasted into (field 0 = Date,
  // 1..N = columns[0..N-1]). A single value with no tabs → normal edit. A single
  // column of values pasted into a data cell fills the row ACROSS (transpose) —
  // a wide vintage row is one date's worth of scores. Anything else fills as a
  // 2D block (across columns, down into new rows).
  const pasteInto = (e, ri, field) => {
    const text = e.clipboardData.getData('text');
    if (!/[\t\n]/.test(text)) return;
    e.preventDefault();
    const fields = ['__date__', ...columns];
    const grid = text.replace(/\r/g, '').split('\n').filter((l) => l.length).map((l) => l.split('\t'));
    const setVal = (row, f, val) => {
      if (!f) return;
      if (f === '__date__') { const d = parseDate(val); if (d) row.as_of = d; }
      else { const n = parseNum(val); row.data[f] = n == null ? '' : n; }
    };
    setRows((prev) => {
      const next = prev.map((r) => ({ as_of: r.as_of, data: { ...r.data } }));
      if (field > 0 && grid.length > 1 && grid.every((g) => g.length === 1)) {
        grid.forEach((g, dc) => setVal(next[ri], fields[field + dc], g[0]));   // column → row across
      } else {
        grid.forEach((cells, dr) => {
          const tr = ri + dr;
          while (next.length <= tr) next.push({ as_of: new Date().toISOString().slice(0, 10), data: {} });
          cells.forEach((val, dc) => setVal(next[tr], fields[field + dc], val));
        });
      }
      return next;
    });
    setMsg('Pasted — review and Save.');
  };

  const deleteRow = (ri) => setRows(rows.filter((_, j) => j !== ri));

  const save = async () => {
    setMsg(null); setError(null);
    try {
      const payload = { rows: rows.map((r) => ({ as_of: r.as_of, data: Object.fromEntries(columns.map((c) => [c, r.data[c] === '' || r.data[c] == null ? null : Number(r.data[c])])) })) };
      const res = await api.post(`/api/tactical-monitor/vintage/${kind}`, payload);
      setMsg(`Saved ${res.data.rows} row(s).`); load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="tm-comp-group">
      <h4>{title}</h4>
      <div className="tm-paste">
        <textarea rows={3} value={paste} onChange={(e) => setPaste(e.target.value)}
          placeholder={`Paste rows from Excel — Date + ${columns.length} tab-separated columns. Header row and accounting negatives (0.89) are handled.`} />
        <div>
          <button className="tm-btn tm-btn-ghost" onClick={appendPaste} disabled={!paste.trim()}>Parse &amp; append</button>
          <button className="tm-btn tm-btn-ghost" onClick={addRow}>+ Row</button>
          <button className="tm-btn" onClick={save} disabled={!rows.length}>Save</button>
          {msg && <span className="tm-muted"> {msg}</span>}
        </div>
      </div>
      {error && <div className="tm-alert">{error}</div>}
      <div className="tm-table-scroll">
        <table className="tm-vintage">
          <thead><tr><th className="tm-del-h" /><th>Date</th>{columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                <td><button className="tm-row-del" title="Delete row" onClick={() => deleteRow(ri)}>✕</button></td>
                <td><input type="date" value={r.as_of} onChange={(e) => setDate(ri, e.target.value)} onPaste={(e) => pasteInto(e, ri, 0)} /></td>
                {columns.map((c, ci) => <td key={c}><input value={r.data[c] ?? ''} onChange={(e) => setCell(ri, c, e.target.value)} onPaste={(e) => pasteInto(e, ri, ci + 1)} /></td>)}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={columns.length + 2} className="tm-muted">No rows. Paste from Excel or add a row.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CmaInputs() {
  return (
    <div>
      <p className="tm-muted">
        Paste the vintage sheets straight from Excel — Date on the left, latest row is the current value, older rows are kept
        for the backtest. Two ways to paste: use the box for whole sheets (Date + columns per row), or copy a row of values and
        paste straight into a grid cell — it spreads across the columns from there (add a row first if the grid is empty).
        Accounting negatives like (0.89) and dashes are handled; values map to the monitor's asset classes on save.
      </p>
      <VintageGrid kind="returns" title="Expected Returns / Returns vintage (z-scores)" />
      <VintageGrid kind="policy" title="Policy vintage" />
    </div>
  );
}

// ── Monitor backtest: weighted signal contribution per asset class ─────
const CONTRIB_PALETTE = ['#f0a500', '#599BE3', '#91CF9B', '#c0392b', '#8e44ad', '#16a085',
  '#e67e22', '#0d6efd', '#d35400', '#7f8c8d', '#27ae60', '#b3261e'];
const sigLabel = (n) => { const q = parseTM(n); return q ? `${q.category[0].toUpperCase()}·${q.indicator}` : n; };

function MonitorBacktest() {
  const scn = useScenario();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [region, setRegion] = useState('Equity World');
  useEffect(() => {
    api.get('/api/tactical-monitor/monitor-backtest', { params: withScn(scn) })
      .then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || e.message));
  }, [scn]);
  const regions = data ? Object.keys(data.regions) : [];
  const r = data?.regions?.[region];
  const merged = r ? r.dates.map((d, i) => {
    const row = { date: d, combined: r.combined[i] };
    r.signals.forEach((s) => { row[s.result_id] = s.contribution[i]; });
    return row;
  }) : [];
  const pct = (v) => (v === null || v === undefined ? '—' : (v * 100).toFixed(1) + '%');

  return (
    <div>
      {error && <div className="tm-alert">{error}</div>}
      {!data && !error && <p className="tm-muted">Loading…</p>}
      {data && (
        <>
          <p className="tm-muted">
            Weighted backtest alpha per asset class = SUMPRODUCT(effective weight, signal cumulative alpha). Effective
            weight = the category's Total-Score weight × the signal's share within its category. Only momentum/growth/
            sentiment carry a backtest alpha stream; at each date the available signals are grossed up to full weight, so
            missing or late-starting signals (e.g. policy) are compensated by the rest. Contributions sum to the combined.
          </p>
          <div className="tm-toolbar">
            {regions.map((rg) => (
              <button key={rg} className={`tm-tab${rg === region ? ' active' : ''}`} onClick={() => setRegion(rg)}>{rg}</button>
            ))}
          </div>
          {(!r || !r.signals.length) ? <p className="tm-muted">No backtest signals for {region}.</p> : (
            <>
              <div className="tm-chart">
                <h4>{region} — weighted backtest alpha (combined {pct(r.combined_final)})</h4>
                <ResponsiveContainer width="100%" height={380}>
                  <LineChart data={merged} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={44} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => (v * 100).toFixed(0) + '%'} />
                    <Tooltip formatter={(v) => pct(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {r.signals.map((s, i) => (
                      <Line key={s.result_id} type="monotone" dataKey={s.result_id} name={sigLabel(s.name)}
                            stroke={CONTRIB_PALETTE[i % CONTRIB_PALETTE.length]} dot={false} strokeWidth={1} />
                    ))}
                    <Line type="monotone" dataKey="combined" name="Combined" stroke={NAVY} dot={false} strokeWidth={2.6} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <table className="tm-comp-table" style={{ minWidth: 520 }}>
                <thead><tr><th>Signal</th><th>Category</th><th style={{ textAlign: 'right' }}>Weight</th><th style={{ textAlign: 'right' }}>Contribution</th></tr></thead>
                <tbody>
                  {r.signals.map((s) => (
                    <tr key={s.result_id}>
                      <td>{sigLabel(s.name)}</td>
                      <td>{s.category}</td>
                      <td style={{ textAlign: 'right' }}>{pct(s.weight_now)}</td>
                      <td style={{ textAlign: 'right', color: (s.final || 0) < 0 ? '#b3261e' : '#1a7f37' }}>{pct(s.final)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700, borderTop: '2px solid #072040' }}>
                    <td>Combined</td><td></td><td style={{ textAlign: 'right' }}>100%</td>
                    <td style={{ textAlign: 'right' }}>{pct(r.combined_final)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── How-it-works modal ────────────────────────────────────────────────
function HelpModal({ onClose }) {
  return (
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal tm-help" onClick={(e) => e.stopPropagation()}>
        <div className="tm-modal-head">
          <h3>How the Tactical Monitor works</h3>
          <button className="tm-btn-link" onClick={onClose}>✕</button>
        </div>
        <div className="tm-help-body">
          <p className="tm-help-lead">
            A self-contained rebuild of the IC “Asset Allocation Monitor”. Every sub-signal is recomputed nightly on the
            portal’s own backtest engine, so the monitor is always current and cloud-hosted — no local Excel at view time.
          </p>

          <h4>1 · The tabs</h4>
          <ul>
            <li><b>Monitor Table</b> — the Asset Allocation Monitor: one block per asset class (Equities Global/AU/EM,
              Bonds Global/AU, Credit Global/AU, FX), a row per signal, colour-coded <b>This Month / Last Month / Δ</b>.
              Green = supportive, amber = headwind, grey = zero. The lower blocks are the category <b>Composites</b>, the
              <b> Total Score</b>, and the <b>Suggested Tilt</b>.</li>
            <li><b>Positioning</b> — the IC positioning-vs-signal panels (Growth / Equities / Bonds / FX): the combined
              signal against the portfolio’s active tilt (target − SAA) and the asset-class return index.</li>
            <li><b>Monitor Backtest</b> — the weighted backtest alpha per asset class, decomposed into each signal’s
              contribution (SUMPRODUCT of effective weight × the signal’s cumulative alpha; missing/late signals are
              grossed up so the rest still sum to 1).</li>
            <li><b>Composition</b> — the editable make-up: which signals feed each asset class, their weights, and the
              category→Total-Score weights. This is where you add / swap / remove signals.</li>
            <li><b>Inputs</b> — the CMA vintage grids (Expected-Returns z-scores and Policy scores) pasted from the
              Capital Market Framework workbook.</li>
          </ul>

          <h4>2 · How a score is built (bottom-up)</h4>
          <ul>
            <li><b>Sub-signals</b> — each signal (e.g. “Growth Barometer”, “MACD Momentum”) is a production backtest that
              outputs a position of −1 / 0 / +1 (Expected Returns is a z-score instead).</li>
            <li><b>Category composites</b> — momentum / growth / sentiment = a weighted average of their sub-signals × 3
              (so roughly ±3). Returns and Policy come from the CMA tables.</li>
            <li><b>Total Score</b> = Σ (category composite × its Total-Score weight). Per asset class the weights are
              roughly Returns .10 / Momentum .35 / Growth .35 / Policy .10–.20 / Sentiment 0–.10.</li>
            <li><b>Suggested Tilt</b> = average of the group’s region Total Scores × 3 ÷ 100 (the IC Summary formula).</li>
          </ul>

          <h4>3 · The five signal groups</h4>
          <ul>
            <li><b>Expected Returns</b> — the CMA z-score per asset class (from the Capital Market Framework). Not a
              backtest; edited in <b>Inputs</b>.</li>
            <li><b>Momentum</b> — Price Momentum (3/6/9-month return) + MACD, computed on each asset class’s total-return
              index (RI, e.g. <code>BMUS10Y(RI)</code>).</li>
            <li><b>Growth</b> — Growth Barometer (the CAI regime), OECD leading indicator, Credit Spreads.</li>
            <li><b>Policy</b> — Easing/Tightening, Inflation-Growth, Yield Curve, Fiscal (CMA / discretionary; edited in
              <b> Inputs</b>).</li>
            <li><b>Sentiment</b> — Bull/Bear (AAII), RSI, Volatility, Put/Call, Earnings Revisions — with expanding-window
              (point-in-time) bands.</li>
          </ul>

          <h4>4 · Where the numbers come from — the nightly refresh (outside the portal)</h4>
          <p>
            Everything is recomputed by a scheduled job on the research machine (Windows Task Scheduler → “datastream
            portal” → <code>refresh_tickers.bat</code>, ~6:23 AM daily). It runs three steps in order, across two Python
            environments:
          </p>
          <ol>
            <li><b>refresh_tickers.py</b> <span className="tm-help-env">.venv (Datastream)</span> — pulls the latest market
              data from LSEG Datastream and <b>incrementally</b> updates <code>Index_Returns_Daily</code> (only new dates;
              it never re-pulls the whole history).</li>
            <li><b>refresh_cai.py</b> <span className="tm-help-env">.venv (Datastream)</span> — rebuilds the Growth
              Barometer (Coincident Activity Index). It fetches the economic inputs (monthly, each country from its own
              start date), recomputes the CAI in-portal (per-sector PCA + GDP-regression weights — this recompute was
              validated <b>one-off</b> against the IC’s <code>Growth_Barometer.sqlite</code> at ≈1.0 correlation, but the
              daily run computes it independently and does not read that file), writes <code>growth_cai</code> /
              <code>growth_cai_monthly</code>, and <b>upserts</b> the CAI columns into <code>Index_Returns_Daily</code>.
              Because the CAI is a full-sample model it must re-fetch its (small) input set each run.</li>
            <li><b>seed_tactical_monitor.py</b> <span className="tm-help-env">portal_env (RDS only)</span> —
              <b> re-backtests every production signal</b> on the fresh data, <b>rebuilds the composites</b> from the
              current composition, and <b>reconciles is_production</b> so only signals actually in the monitor are flagged
              production. Your composition edits are preserved (defaults are not re-imposed).</li>
          </ol>
          <p className="tm-help-note">
            So market data + CAI refresh nightly, then every monitor signal is re-run against them. The portal only ever
            reads the results from RDS — no Datastream call at view time.
          </p>

          <h4>5 · Switching signals in and out (Composition)</h4>
          <ul>
            <li>The grid mirrors the table: rows = signals, columns = asset classes, cells = the weight each signal
              contributes. Type a weight to add, clear it (blank/0) to remove; weights are normalised when the composite
              rebuilds.</li>
            <li><b>+</b> in a blank cell opens the picker — <b>every backtest signal</b> (production or not), sortable by any
              column, defaulted to the ones on that asset class’s return leg — pick one to incorporate.</li>
            <li><b>⇄</b> on a filled cell swaps its signal for another via the same picker.</li>
            <li><b>ⓘ</b> opens a signal’s audit: config, alpha stats, where it’s used, and its position / cumulative-alpha /
              drawdown charts, with a button to open it in the Signal Dashboard.</li>
            <li>The category→Total-Score weights and the Policy / Expected-Returns weights are editable at the bottom. You
              can also set an editable <b>Note</b> per signal (shows in the table’s Note column).</li>
            <li>Every change rebuilds the affected composite via the engine and re-flows the table. On the next nightly run
              the signal is re-backtested and, being in the composition, is flagged production.</li>
          </ul>

          <h4>6 · Editing the CMA inputs (Inputs)</h4>
          <ul>
            <li>Two paste grids — <b>Expected Returns / Returns z-scores</b> and <b>Policy</b> — mirror the Capital Market
              Framework vintage sheets.</li>
            <li>Paste a whole row (or sheet) straight from Excel; a single copied <i>column</i> fills across the row; <b>✕</b>
              deletes a row. Accounting negatives <code>(0.89)</code> and dashes are handled.</li>
            <li>The latest-dated row is the current value; older rows are kept so the backtest sees the full vintage history.</li>
          </ul>

          <h4>7 · “This Month” is live</h4>
          <p>
            The table’s <b>This Month</b> is the current month-end (so an August Expected-Returns update shows immediately),
            <b> Last Month</b> is the prior month-end, and <b>Δ</b> is the change. Signals snap to the latest stored position.
          </p>

          <h4>Data sources</h4>
          <table className="tm-help-tbl"><tbody>
            <tr><td>LSEG Datastream</td><td>prices, economic series, forward EPS (market data + CAI inputs)</td></tr>
            <tr><td>Capital Market Framework.xlsm</td><td>Expected-Returns + Policy vintages (pasted in Inputs)</td></tr>
            <tr><td>Growth_Barometer.sqlite</td><td>the IC’s original CAI. The portal recomputes the CAI itself each night and does <b>not</b> read this file; it was used as a <b>one-off</b> validation reference (matched to ≈1.0), re-checkable on demand with <code>refresh_cai&nbsp;--compare</code></td></tr>
            <tr><td>portfolio.Shadow_Data + SAA</td><td>the Positioning tab’s active weights (target − SAA)</td></tr>
          </tbody></table>
        </div>
        <div className="tm-modal-foot">
          <span className="tm-muted">Production signals are re-backtested nightly; the portal reads results from RDS.</span>
          <button className="tm-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Shell ─────────────────────────────────────────────────────────────
export default function TacticalMonitorApp() {
  const [tab, setTab] = useState('table');
  const [help, setHelp] = useState(false);
  const [scenario, setScenario] = useState('production');   // 'production' | 'dev'
  const [devBusy, setDevBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);          // bump to force a reload after reset

  // Switch to dev: clone production into the dev scenario on first entry (only if the
  // dev composition is empty), so dev starts byte-identical to the live monitor.
  const goDev = async () => {
    setDevBusy(true);
    try {
      const c = await api.get('/api/tactical-monitor/composition', { params: { scenario: 'dev' } });
      if (!(c.data.members || []).length) {
        await api.post('/api/tactical-monitor/scenario/reset', { scenario: 'dev' });
      }
      setScenario('dev');
    } catch (e) {
      window.alert('Could not switch to dev: ' + (e.response?.data?.detail || e.message));
    } finally { setDevBusy(false); }
  };

  const resetDev = async () => {
    if (!window.confirm('Reset the dev scenario to match production? This discards all dev edits.')) return;
    setDevBusy(true);
    try {
      await api.post('/api/tactical-monitor/scenario/reset', { scenario: 'dev' });
      setRefreshKey((k) => k + 1);   // remount tab content so it reloads the fresh clone
    } catch (e) {
      window.alert('Reset failed: ' + (e.response?.data?.detail || e.message));
    } finally { setDevBusy(false); }
  };

  const toggleScenario = () => (scenario === 'dev' ? setScenario('production') : goDev());

  // The CMA Inputs tab edits shared (production) CMA tables, so it isn't offered in
  // the dev scenario; fall the active tab back to the table if we land on it in dev.
  const TABS = [['table', 'Monitor Table'], ['decision', 'Trade Decision'], ['positioning', 'Positioning'], ['backtest', 'Monitor Backtest'], ['composition', 'Composition']]
    .concat(scenario === 'dev' ? [] : [['inputs', 'Inputs']]);
  const activeTab = TABS.some(([id]) => id === tab) ? tab : 'table';

  return (
    <ScenarioContext.Provider value={scenario}>
      <div className="tm-app">
        {help && <HelpModal onClose={() => setHelp(false)} />}
        {scenario === 'dev' && (
          <div className="tm-dev-banner">
            <span><strong>DEV scenario</strong> — swap signals and reweight to trial alternatives. Changes here do not affect the live monitor.</span>
            <button className="tm-btn tm-btn-ghost" onClick={resetDev} disabled={devBusy}>Reset dev to production</button>
          </div>
        )}
        <div className="tm-tabs">
          {TABS.map(([id, label]) => (
            <button key={id} className={`tm-tab${activeTab === id ? ' active' : ''}`} onClick={() => setTab(id)}>{label}</button>
          ))}
          <button className="tm-help-btn" onClick={() => setHelp(true)} title="How it works">ⓘ How it works</button>
          <button className={`tm-scn-toggle${scenario === 'dev' ? ' dev' : ''}`} disabled={devBusy}
                  onClick={toggleScenario}
                  title={scenario === 'dev' ? 'Return to the live production monitor' : 'Open an isolated dev copy to trial alternative signals'}>
            {devBusy ? 'Loading…' : scenario === 'dev' ? '← Switch to Production' : 'Switch to Dev →'}
          </button>
        </div>
        <div key={`${scenario}:${refreshKey}`}>
          {activeTab === 'table' && <MonitorTable />}
          {activeTab === 'decision' && <TradeDecision />}
          {activeTab === 'positioning' && <Positioning />}
          {activeTab === 'backtest' && <MonitorBacktest />}
          {activeTab === 'composition' && <Composition />}
          {activeTab === 'inputs' && <CmaInputs />}
        </div>
      </div>
    </ScenarioContext.Provider>
  );
}
